'use client'

import React from 'react'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { RequestDocument } from 'graphql-request'
import { TOP_SELLING, FOR_MANUFACTURER, BY_IDS, BY_ACTIVITY_ID } from '../utils/aliases'
import { GET_PRODUCTS_BY_IDS } from '@dustin-web/microsite-data/src/graphql/product/get-products-by-ids.graphql'
import { GET_ACTIVITY_PRODUCTS } from '@dustin-web/microsite-data/src/graphql/product/get-activity-products.graphql'
import { WrappedComponentProps, WrapperProps, ArgsType, QueryType } from '../types/types'
import {
  GetVarAndQueryTopSelling,
  GetVarAndQueryForManufacturer,
} from '../utils/vars-and-querys-generators'
import productsDataConverter from '../utils/products-data-converter'
import {
  GetProductsForManufacturerQuery,
  GetTopSellingProductsQuery,
  GetProductsByIdsQuery,
  GetActivityProductsQuery,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'

export const withGods = (WrappedComponent: React.ComponentType<WrappedComponentProps>) => {
  const Wrapped = (props: WrapperProps) => {
    const { numberOfProducts, criterias, isAvailable } = props
    const deffaultNumberOfProducts = numberOfProducts ? numberOfProducts : 20
    const { properties, type: criteriaType } = criterias?.[0] ?? {}
    const { type: subCriteriaType, properties: subCriteriaProps } =
      properties?.subCriterias?.[0] ?? {}

    // defining args and queries
    let query: RequestDocument = ''
    let variables: ArgsType = {}
    switch (criteriaType) {
      case TOP_SELLING: {
        const { query: TSQuery, variables: TSVariables } = GetVarAndQueryTopSelling(
          subCriteriaType,
          subCriteriaProps,
          deffaultNumberOfProducts,
          isAvailable
        )
        query = TSQuery
        variables = TSVariables
        break
      }
      case FOR_MANUFACTURER: {
        const { query: MFQuery, variables: MFVariables } = GetVarAndQueryForManufacturer(
          subCriteriaType,
          subCriteriaProps,
          deffaultNumberOfProducts,
          isAvailable
        )
        query = MFQuery
        variables = MFVariables
        break
      }
      case BY_IDS:
        query = GET_PRODUCTS_BY_IDS
        variables = {
          productErpIdentifiers: properties?.productIDs || [],
        }
        break
      case BY_ACTIVITY_ID:
        query = GET_ACTIVITY_PRODUCTS
        variables = {
          activityId: properties?.productActivityID,
        }
        break
    }

    const { data, isValidating } = useGraphql<QueryType, ArgsType>(query, {
      variables: variables,
    })

    // get products from different endpoints
    let products
    switch (criteriaType) {
      case TOP_SELLING: {
        const TSData = data as GetTopSellingProductsQuery
        products = TSData?.topSellingProducts?.productReferences ?? []
        break
      }
      case FOR_MANUFACTURER: {
        const MFData = data as GetProductsForManufacturerQuery
        products = MFData?.manufacturerProductSearch?.productReferences ?? []
        break
      }
      case BY_IDS: {
        const ByIDsData = data as GetProductsByIdsQuery
        const ByIDsProducts = ByIDsData?.products
        products = ByIDsProducts ? productsDataConverter(ByIDsProducts) : []
        break
      }
      case BY_ACTIVITY_ID: {
        const ByActivityIDData = data as GetActivityProductsQuery
        const ByActivityIDProducts = ByActivityIDData?.activity?.products
        products = ByActivityIDProducts ? productsDataConverter(ByActivityIDProducts) : []
        break
      }
    }

    return <WrappedComponent {...props} products={products} isLoading={isValidating} />
  }

  Wrapped.displayName = `withGods(${WrappedComponent.displayName || WrappedComponent.name})`

  return Wrapped
}
